import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import { Box, Menu, MenuItem } from '@mui/material';
import { VisibleCards } from 'spw-programming-projects/reports/exportables/hooks';
import { ProgramSummaryContext } from 'spw-programming-projects/program-summary/program-summary-context';
import {
  setAllValuesToFalse,
  updateCategoryCadsVisibility,
} from 'spw-projects/reports/utils';
import { ScenarioHeader } from 'spw-components/downloads/image-header';
import { selectCurrentScenario } from 'spw-redux/modules/calculators/selectors';
import { useSelector } from 'react-redux';
import {
  selectCurrentBuilding,
  selectFirstBuilding,
} from 'spw-redux/modules/projects/selectors';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import { camelCase } from 'lodash';

const HeaderContainer = styled.div``;

const MENU_ITEMS = [
  { id: 'keyDataPoints', label: 'Key Data Points' },
  { id: 'headcountAnalysis', label: 'Headcount Analysis' },
  { id: 'seatCountAnalysis', label: 'Seat Count Analysis' },
  { id: 'headcountUsf', label: 'Headcount USF' },
  { id: 'seatCountUsf', label: 'Seat Count USF' },
  { id: 'totalProgramAllocation', label: 'Total Program Allocation' },
  { id: 'spaceTypeAllocation', label: 'Space Type Allocation' },
  {
    id: 'spaceTypeAllocationSubtotalProgramUsf',
    label: 'Space Type Allocation Subtotal Program USF',
  },
  { id: 'spaceTypeAllocationIWUsf', label: 'Space Type Allocation - IWS USF' },
  { id: 'seatTypeMixIW', label: 'Seat Type Mix - IW' },
  { id: 'squareFootageBreakdown', label: 'Square Footage Breakdown' },
  { id: 'meetingSeatRatio', label: 'Meeting Seat Ratio' },
  { id: 'projectOverview', label: 'Project Overview' },
  { id: 'programSummaryReport', label: 'Program Summary Report' },
];

export const Header = ({
  showProjectName = false,
}: {
  showProjectName?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleCardsButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const { visibleCards, setVisibleCards } = useContext(ProgramSummaryContext);
  const scenario = useSelector(selectCurrentScenario);
  const building =
    useSelector(selectCurrentBuilding) || useSelector(selectFirstBuilding);

  const handleCardMenuItemClick = (card: keyof VisibleCards) => {
    setAnchorEl(null);
    setVisibleCards((prevVisibleCards: VisibleCards) => {
      const newVisibleCards = setAllValuesToFalse(
        prevVisibleCards
      ) as VisibleCards;

      return { ...newVisibleCards, [card]: true };
    });
  };

  const handleCategoryCardMenuItemClick = (category: string, key: string) => {
    setAnchorEl(null);
    setVisibleCards((prevVisibleCards: VisibleCards) => {
      return updateCategoryCadsVisibility(prevVisibleCards, category, key);
    });
  };

  const showHeader = useMemo(() => {
    return (
      visibleCards.programSummaryReport ||
      visibleCards.categoryUsfAndSeatMix[INDIVIDUAL_WORKSPACES_CATEGORY]
        .usfAndSeatMix ||
      [
        MEETING_SPACES_CATEGORY,
        AMENITY_SPACES_CATEGORY,
        SUPPORT_SPACES_CATEGORY,
      ].some((category) => {
        return visibleCards.categoryUsfAndSeatMix[category].usfMixAndRatios;
      })
    );
  }, [visibleCards]);

  return (
    <HeaderContainer>
      <Box
        display="flex"
        style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}
      >
        <Button onClick={handleCardsButtonClick} id="show-cards" />
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)}>
          {MENU_ITEMS.map((menuItem) => {
            return (
              <MenuItem
                id={menuItem.id}
                key={menuItem.id}
                onClick={() => {
                  handleCardMenuItemClick(menuItem.id as keyof VisibleCards);
                }}
              >
                {menuItem.label}
              </MenuItem>
            );
          })}
          {[
            'usfMixSeatMixAndSubtotals',
            'usfAndSeatMix',
            'usfMix',
            'seatMix',
            'seatMixSubtotals',
          ].map((cardType) => {
            const category = INDIVIDUAL_WORKSPACES_CATEGORY;
            return (
              <MenuItem
                key={camelCase(`${category}_${cardType}`)}
                id={camelCase(`${category}_${cardType}`)}
                onClick={() => {
                  handleCategoryCardMenuItemClick(category, cardType);
                }}
              >
                {category} {cardType}
              </MenuItem>
            );
          })}
          {[
            MEETING_SPACES_CATEGORY,
            AMENITY_SPACES_CATEGORY,
            SUPPORT_SPACES_CATEGORY,
          ].map((category) => {
            return ['usfMixAndRatios', 'usfMix', 'ratios'].map((cardType) => {
              return (
                <MenuItem
                  key={camelCase(`${category}_${cardType}`)}
                  id={camelCase(`${category}_${cardType}`)}
                  onClick={() => {
                    handleCategoryCardMenuItemClick(category, cardType);
                  }}
                >
                  {category} {cardType}
                </MenuItem>
              );
            });
          })}
        </Menu>
      </Box>
      {showHeader && (
        <ScenarioHeader
          showProjectName={showProjectName}
          scenario={scenario}
          building={building}
          styles={{ paddingLeft: 19 }}
        />
      )}
    </HeaderContainer>
  );
};
